/** xoshiro128** seeded PRNG. Its state lives inside the simulation state (SPEC §3.4). */
export type RngState = [number, number, number, number];

const rotl = (x: number, k: number): number => ((x << k) | (x >>> (32 - k))) >>> 0;

/** splitmix32 → 4 words, so any 32-bit seed gives a well-mixed state. */
export const seedRng = (seed: number): RngState => {
  let z = seed >>> 0;
  const out: number[] = [];
  for (let i = 0; i < 4; i++) {
    z = (z + 0x9e3779b9) >>> 0;
    let t = z;
    t = Math.imul(t ^ (t >>> 16), 0x85ebca6b) >>> 0;
    t = Math.imul(t ^ (t >>> 13), 0xc2b2ae35) >>> 0;
    t = (t ^ (t >>> 16)) >>> 0;
    out.push(t === 0 && i === 0 ? 1 : t);
  }
  return out as RngState;
};

/** Next uint32. Mutates the state in place. */
export const rngNext = (s: RngState): number => {
  const s0 = s[0];
  const s1 = s[1];
  const s2 = s[2];
  const s3 = s[3];
  const result = Math.imul(rotl(Math.imul(s1, 5) >>> 0, 7), 9) >>> 0;
  const t = (s1 << 9) >>> 0;
  const n2 = (s2 ^ s0) >>> 0;
  const n3 = (s3 ^ s1) >>> 0;
  const n1 = (s1 ^ n2) >>> 0;
  const n0 = (s0 ^ n3) >>> 0;
  s[0] = n0;
  s[1] = n1;
  s[2] = (n2 ^ t) >>> 0;
  s[3] = rotl(n3, 11);
  return result;
};

/** Uniform integer in [0, n). */
export const rngInt = (s: RngState, n: number): number => (n <= 1 ? 0 : rngNext(s) % n);
/** Uniform integer in [lo, hi] inclusive. */
export const rngRange = (s: RngState, lo: number, hi: number): number => lo + rngInt(s, hi - lo + 1);
/** True with probability bp / 10000. */
export const rngChance = (s: RngState, bp: number): boolean => rngInt(s, 10000) < bp;
/** Picks a key by integer weights; keys are iterated in the given order. */
export const rngWeighted = <K>(s: RngState, entries: readonly (readonly [K, number])[]): K => {
  let total = 0;
  for (const [, w] of entries) total += w;
  let roll = rngInt(s, total);
  for (const [k, w] of entries) {
    if (roll < w) return k;
    roll -= w;
  }
  return (entries[entries.length - 1] as readonly [K, number])[0];
};
/** Derive a child seed (for sub-simulations) without disturbing determinism. */
export const deriveSeed = (seed: number, salt: number): number => {
  let h = (seed ^ Math.imul(salt + 0x6d2b79f5, 0x9e3779b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39) >>> 0;
  return (h ^ (h >>> 15)) >>> 0;
};
